import { useEffect } from 'react';
import { useCookieConsent } from '../components/CookieContext';
import googleAnalyticsService from '../analytics/GoogleAnalyticsService';
import marketingServicesManager from '../analytics/MarketingServicesManager';
import { getMarketingServicesConfig, isMarketingServiceEnabled } from '../analytics/config';
import { logger } from '../analytics/utils';

// Hook per gestire tutti i servizi di terze parti basati sui cookie
export const useThirdPartyServices = () => {
  const { isAccepted, isInitialized } = useCookieConsent();

  // Inizializza i servizi di marketing
  useEffect(() => {
    if (!isInitialized) return;

    const marketingConfig = getMarketingServicesConfig();
    
    // Registra i servizi di marketing abilitati
    Object.entries(marketingConfig).forEach(([serviceName, config]) => {
      if (isMarketingServiceEnabled(serviceName)) {
        marketingServicesManager.registerService(serviceName.toLowerCase(), {
          scriptUrl: config.SCRIPT_URL,
          scriptId: config.SCRIPT_ID,
          // Configurazioni specifiche per servizio
          ...config
        });
      }
    });
  }, [isInitialized]);

  // Gestisce i servizi analytics
  useEffect(() => {
    if (!isInitialized) return;

    if (isAccepted('analytics')) {
      logger.log('Analytics consent granted, loading Google Analytics');
      googleAnalyticsService.loadScript();
    } else {
      logger.log('Analytics consent revoked, removing Google Analytics');
      googleAnalyticsService.remove();
    }
  }, [isAccepted('analytics'), isInitialized]);

  // Gestisce i servizi di marketing
  useEffect(() => {
    if (!isInitialized) return;

    if (isAccepted('marketing')) {
      logger.log('Marketing consent granted, loading marketing services');
      marketingServicesManager.loadAllServices();
    } else {
      logger.log('Marketing consent revoked, removing marketing services');
      marketingServicesManager.removeAllServices();
    }
  }, [isAccepted('marketing'), isInitialized]);

  // Aggiorna il consenso per tutti i servizi
  useEffect(() => {
    if (!isInitialized) return;

    const consentSettings = {
      analytics: isAccepted('analytics'),
      marketing: isAccepted('marketing'),
      functionality: isAccepted('functionality'),
      personalization: isAccepted('personalization')
    };

    // Aggiorna consenso Google Analytics
    googleAnalyticsService.updateConsent(consentSettings);
    
    // Aggiorna consenso servizi marketing
    marketingServicesManager.updateConsent(consentSettings);
    
    logger.log('Consent updated for all services', consentSettings);
  }, [
    isAccepted('analytics'),
    isAccepted('marketing'),
    isAccepted('functionality'),
    isAccepted('personalization'),
    isInitialized
  ]);

  // Funzioni per gestire servizi specifici
  const loadMarketingService = (serviceId) => {
    if (isAccepted('marketing')) {
      return marketingServicesManager.loadService(serviceId);
    }
    logger.warn(`Cannot load marketing service ${serviceId}: marketing consent not granted`);
  };

  const removeMarketingService = (serviceId) => {
    marketingServicesManager.removeService(serviceId);
  };

  const getServicesStatus = () => {
    return {
      analytics: googleAnalyticsService.getStatus(),
      marketing: marketingServicesManager.getStatus(),
      consent: {
        analytics: isAccepted('analytics'),
        marketing: isAccepted('marketing'),
        functionality: isAccepted('functionality'),
        personalization: isAccepted('personalization')
      }
    };
  };

  return {
    loadMarketingService,
    removeMarketingService,
    getServicesStatus,
    // Espone i manager per uso avanzato
    googleAnalyticsService,
    marketingServicesManager
  };
}; 